import { useMemo, useState } from "react";
import {
  Line,
  LineChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
  Brush,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { useMacroEngine, type MacroEngineIndicator } from "@/hooks/useMacroEngine";
import { format } from "date-fns";
import { MacroChartTooltip } from "@/components/macro/MacroChartTooltip";

type UIIndicator = "cpi" | "fedFunds";

const INDICATOR_META: Record<UIIndicator, { label: string; api: MacroEngineIndicator }>= {
  cpi: { label: "Inflação (CPI)", api: "CPI" },
  fedFunds: { label: "Juros (Fed Funds)", api: "Fed Funds" },
};

function formatAxisValue(v: unknown, unit: "index" | "percent") {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "—";
  if (unit === "percent") return `${n.toFixed(2)}%`;
  return n.toFixed(2);
}

function formatMonthLabel(isoDate: string) {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return format(d, "MMM yy");
}

export default function Macro() {
  const [indicator, setIndicator] = useState<UIIndicator>("cpi");
  const years = 5;

  const { data, isLoading, error } = useMacroEngine(INDICATOR_META[indicator].api, years);

  const unitLeft: "index" | "percent" = indicator === "cpi" ? "index" : "percent";
  const unitRight: "percent" = "percent";

  const chartData = useMemo(() => {
    return (data ?? []).map((p) => ({
      date: p.date,
      realValue: p.realValue,
      marketExpectation: p.marketExpectation,
    }));
  }, [data]);

  const [brush, setBrush] = useState<{ startIndex: number; endIndex: number }>({
    startIndex: 0,
    endIndex: Math.max(0, chartData.length - 1),
  });

  // Keep brush endIndex in sync when the dataset size changes
  const effectiveBrush = useMemo(() => {
    const max = Math.max(0, chartData.length - 1);
    const startIndex = Math.min(Math.max(0, brush.startIndex), max);
    const endIndex = Math.min(Math.max(startIndex, brush.endIndex), max);
    return { startIndex, endIndex };
  }, [brush.endIndex, brush.startIndex, chartData.length]);

  const visibleData = useMemo(() => {
    if (!chartData.length) return [];
    return chartData.slice(effectiveBrush.startIndex, effectiveBrush.endIndex + 1);
  }, [chartData, effectiveBrush.endIndex, effectiveBrush.startIndex]);

  const hasAnyPoly = useMemo(
    () => (chartData ?? []).some((d) => typeof d.marketExpectation === "number"),
    [chartData]
  );

  return (
    <main className="container mx-auto p-6 space-y-6">
      <header className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Macro</h1>
            <p className="text-sm text-muted-foreground">
              Comparação FRED vs expectativa de mercado (Polymarket) — período padrão {years}Y.
            </p>
          </div>
        </div>

        <Tabs value={indicator} onValueChange={(v) => setIndicator(v as UIIndicator)}>
          <TabsList>
            <TabsTrigger value="cpi">{INDICATOR_META.cpi.label}</TabsTrigger>
            <TabsTrigger value="fedFunds">{INDICATOR_META.fedFunds.label}</TabsTrigger>
          </TabsList>
          <TabsContent value={indicator} />
        </Tabs>
      </header>

      <Card className="bg-card/50 backdrop-blur border-border/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{INDICATOR_META[indicator].label}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-8 w-56" />
              <Skeleton className="h-[340px] w-full" />
            </div>
          ) : error ? (
            <div className="text-sm text-destructive">
              {(error as Error).message}
            </div>
          ) : chartData.length === 0 ? (
            <div className="text-sm text-muted-foreground">Sem dados disponíveis.</div>
          ) : (
            <div className="space-y-3">
              {!hasAnyPoly && (
                <div className="text-xs text-muted-foreground">
                  Sem expectativa Polymarket disponível neste período (a linha roxa pode não aparecer).
                </div>
              )}

              <div className="min-h-[400px] h-[420px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={visibleData} margin={{ top: 10, right: 10, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatMonthLabel}
                      interval="preserveStartEnd"
                      minTickGap={32}
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="left"
                      tickFormatter={(v) => formatAxisValue(v, unitLeft)}
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={64}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tickFormatter={(v) => formatAxisValue(v, unitRight)}
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={64}
                    />
                    <Tooltip content={<MacroChartTooltip unitLeft={unitLeft} unitRight={unitRight} />} />
                    <Legend
                      formatter={(value) => {
                        const v = String(value);
                        const label =
                          v === "realValue"
                            ? "Dado Real (FRED)"
                            : v === "marketExpectation"
                              ? "Expectativa de Mercado (Polymarket)"
                              : v;
                        return <span className="text-xs text-muted-foreground">{label}</span>;
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="realValue"
                      yAxisId="left"
                      stroke="hsl(var(--foreground))"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 6, strokeWidth: 0 }}
                      isAnimationActive={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="marketExpectation"
                      yAxisId="right"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      strokeDasharray="6 6"
                      dot={false}
                      activeDot={{ r: 6, strokeWidth: 0 }}
                      connectNulls={false}
                      isAnimationActive={false}
                    />

                    <Brush
                      dataKey="date"
                      height={35}
                      stroke="hsl(var(--primary))"
                      fill="hsl(var(--muted))"
                      tickFormatter={formatMonthLabel}
                      startIndex={effectiveBrush.startIndex}
                      endIndex={effectiveBrush.endIndex}
                      travellerWidth={10}
                      onChange={(r) => {
                        if (!r) return;
                        const startIndex = typeof r.startIndex === "number" ? r.startIndex : 0;
                        const endIndex =
                          typeof r.endIndex === "number" ? r.endIndex : Math.max(0, chartData.length - 1);
                        setBrush({ startIndex, endIndex });
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
